import _isEmpty from 'underscore/cjs/isEmpty.js'

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js'

import {
  DESTINATIONS
} from '~/constants/destinations.constants'

import {
  AGE_RANGE,
  EMAIL_REQUIRED_ERROR,
  ERROR_MAP
} from '~/constants/funnel/travel-insurance'

import {
  validateAge,
  emailValidator
} from '~/helpers/validator'

import { useUtmsStore } from './utms'

dayjs.extend(customParseFormat)
dayjs.extend(isSameOrAfter)

export const useTravelInsuranceStore = defineStore('travel-insurance', {
  state: () => ({
    UUID: '',

    form: {},
    formUpdate: {},

    tempEmail: null,

    quoteProducts: []
  }),

  getters: {
    leadParams () {
      return this.generateLead(this.form)
    },

    leadUpdateParams () {
      const newObj = {}
      const curr = JSON.parse(JSON.stringify(this.form))
      const updated = JSON.parse(JSON.stringify(this.formUpdate))

      /**
       * compares curr and updated objects 
       * and only returns the changed keys
       */
      for (const key in updated) {
        if (!Object.hasOwn(updated, key)) {
          continue
        }
        
        const currEl = curr[key]
        const updatedEl = updated[key]

        if (JSON.stringify(currEl) !== JSON.stringify(updatedEl)) {
          newObj[key] = updatedEl
        }

        if (key === 'email' && this.tempEmail && !updatedEl && !currEl) {
          newObj[key] = null
        }
      }
      
      return this.generateLead(newObj)
    },

    filters () {
      const form = this.leadParams

      const cruiseCover = form?.extraCovers && form.extraCovers.includes('Cruise')
      const snowSportsCover = form?.extraCovers && form.extraCovers.includes('Snow Sports')

      return {
        tripCancellation: form?.tripCancellation,
        luggageCover: form?.luggageCover,
        maxExcess: form?.maxExcess,
        rentalCarCover: form?.rentalCarCover,
        cruiseCover,
        snowSportsCover
      }
    }
  },

  actions: {
    generateLead (form) {
      form = JSON.parse(JSON.stringify(form))

      if (_isEmpty(form)) {
        return {}
      }

      const email = (() => {
        if (this.tempEmail && !form.email) {
          return null
        }

        return form.email || null
      })()

      return removeFalseyObj({
        ...form,
        adultAges: !_isEmpty(form.adultAges)
          ? form.adultAges.replace(/ /g, '')
          : null,
        dependantAges: !_isEmpty(form.dependantAges)
          ? form.dependantAges.replace(/ /g, '')
          : null,
        quoteType: strToCapitalize(form.quoteType),
        destinations: (() => {
          if (_isEmpty(form.destinations)) {
            return null
          }

          return form.destinations.map(e => e.value)
        })(),
        email
      }, null, true)
    },

    async createLead (payload) {
      const utmsStore = useUtmsStore()

      const response = await useFetch('/api/lead/create', {
        method: 'post',
        body: {
          vertical: 'Travel Insurance',
          ...utmsStore.utms,
          ...payload,
          form_page: import.meta.client && window.location.href,
          landing_page: utmsStore.GET_UTMS_LANDING_PAGE,
          email_provided: !!payload.email,
          state: 'nsw' // hardcoded state as API requires it, but it doesn't affect the results
        }
      }).then(res => {
        const temp = res?.data.value

        if (temp?.error) {
          throw new Error(temp?.error?.message ?? Object.values(temp?.error).join(', '))
        }

        const data = temp?.data

        this.UUID = data?.lead_uuid ?? undefined

        return this.UUID
      }).catch(error => {
        throw new Error(error)
      })

      return response
    },

    async updateLead (payload) {
      const response = await $fetch('/api/lead/update', {
        method: 'patch',
        body: {
          vertical: 'Travel Insurance',
          ...payload
        }
      }).then(res => {
        if (res?.error) {
          throw new Error(res?.error?.message ?? Object.values(res?.error).join(', '))
        }

        return res?.data?.lead_uuid ?? undefined
      }).catch(error => {
        throw new Error(error)
      })

      return response
    },

    async getQuotes (UUID) {
      function transformAgeStr (age) {
        const temp = age.includes(',')
          ? age.split(',')
          : [age]
        
        return temp.join(', ')
      }
        
      const response = await $fetch('/api/travel-insurance/qoute-results/', {
        method: 'GET',
        query: {
          UUID
        }
      })
        .then(res => {
          const filters = Object.assign({}, res?.filters)

          this.UUID = UUID

          this.form.tripCancellation = filters?.tripCancellation ?? null
          this.form.luggageCover = filters?.luggageCover ?? null
          this.form.maxExcess = filters?.maxExcess ?? null
          this.form.rentalCarCover = filters?.rentalCarCover ?? null
          this.form.extraCovers = filters?.extraCovers ?? []
          
          this.form.quoteType = filters?.quoteType
            ? filters?.quoteType.toLowerCase()
            : filters?.quoteType

          this.form.destinations = (() => {
            const dest = isObject(filters?.destinations)
              ? Object.values(filters?.destinations)
              : filters?.destinations

            return dest.reduce((acc, curr) => {
              const item = DESTINATIONS.find(e => e.value === curr)
              if (item) {
                acc.push(item)
              }
              return acc
            }, [])
          })()

          this.form.adultAges = transformAgeStr(filters?.adultAges)
          this.form.dependantAges = filters?.dependantAges
            ? transformAgeStr(filters?.dependantAges)
            : filters?.dependantAges
          
          this.form.maxTripLength = filters?.maxTripLength

          this.form.commencementDate = filters?.commencementDate
          this.form.leavingDate = filters?.leavingDate
          this.form.returnDate = filters?.returnDate

          if (filters?.email?.includes('dispostable')) {
            this.tempEmail = filters?.email
          }

          this.form.email = filters?.email?.includes('dispostable')
            ? null
            : filters?.email
          
          this.formUpdate = JSON.parse(JSON.stringify(this.form))
          
          this.quoteProducts = res?.products ?? []
          return res
        })
        .catch(error => {
          throw new Error(error)
        })
      
      return response
    },

    handleAgeInput (val) {
      return val
        .split(',')
        .map(age => age.trim().replace(/\b0+(\d)/g, '$1').replace(/[^\d,]/g, ''))
        .join(', ')
    },

    handleValidateAge (type, age) {
      if (!['adultAges', 'dependantAges'].includes(type)) {
        return false
      }
    
      if (
        type === 'adultAges' &&
        _isEmpty(age)
      ) {
        return false
      }
    
      if (
        type === 'dependantAges' &
        _isEmpty(age)
      ) {
        return true
      }
    
      const range = getKey(type, {
        adultAges: AGE_RANGE?.adult,
        dependantAges: AGE_RANGE?.dependant
      })
    
      const temp = age.includes(',')
        ? age.split(',')
        : [age]
        
      const ageArr = temp.map(item => item.trim())
    
      return ageArr.every(item => !(isNullish(item) || item === '') && validateAge(item, range))
    },

    handleValidateForm (formModel, validation, formKey, { emailRequired = false } = {}) {
      if (_isEmpty(formModel) || _isEmpty(validation)) {
        return 
      }

      const validate = (question, value) => { 
        const validationFunc = getKey(question, {
          default: () => !_isEmpty(value),
          extraCovers: () => true,
          maxTripLength: () => !!value,

          'adultAges, dependantAges': () => this.handleValidateAge(question, value),

          email: () => {
            // CC-40297 Variant: a blank email blocks submit
            if (emailRequired && !value?.trim()) {
              return { valid: false, errorMessage: EMAIL_REQUIRED_ERROR }
            }

            if (value) {
              return emailValidator(value)
            }

            return true
          },

          'leavingDate, returnDate, commencementDate': () => { 
            if (!value || value.length < 10) {
              return false
            }

            const date = dayjs(value, 'DD/MM/YYYY', true)
            const now = dayjs()

            const isDate = date.isValid()

            if (formKey === 'returnDate') {
              const compareDate = formModel?.leavingDate
                ? dayjs(formModel?.leavingDate, 'DD/MM/YYYY', true)
                : now

              return isDate && date.isSameOrAfter(compareDate, 'day')
            }

            return isDate && date.isSameOrAfter(now, 'day') 
          }
        })

        if (typeof validationFunc === 'function') {
          return validationFunc?.(value)
        }
      }

      if (formKey) {
        const currVal = formModel?.[formKey]
        const isValid = validate(formKey, currVal)

        validation[formKey] = { 
          isSuccess: isValid?.valid ?? isValid,
          errorMessage: isValid?.valid ?? isValid
            ? ''
            : isValid?.errorMessage ?? ERROR_MAP?.[formKey]
        }
        
        return
      }

      const KEYS = Object.keys(validation)

      for (let i = 0; i < KEYS.length; i++) {
        const key = KEYS[i]

        const currVal = formModel?.[key]
        const isValid = validate(key, currVal)

        validation[key] = {
          isSuccess: isValid?.valid ?? isValid,
          errorMessage: isValid?.valid ?? isValid
            ? ''
            : isValid?.errorMessage ?? ERROR_MAP?.[key]
        }
      }
    }
  }
})
